import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const questionTypes = [
  { id: 'grammar_cloze', name: 'Grammar Cloze' },
  { id: 'vocabulary_cloze', name: 'Vocabulary Cloze' },
  { id: 'grammar_mcq', name: 'Grammar MCQ' },
  { id: 'vocabulary_mcq', name: 'Vocabulary MCQ' },
  { id: 'synthesis', name: 'Synthesis & Transformation' },
  { id: 'editing', name: 'Editing' },
  { id: 'comprehension_cloze', name: 'Comprehension Cloze' },
  { id: 'antonyms_synonyms', name: 'Antonyms & Synonyms' },
  { id: 'regular_irregular_verbs', name: 'Regular & Irregular Verbs' }
];

const AIGeneratorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questionType, setQuestionType] = useState('grammar_cloze');
  const [level, setLevel] = useState(user?.level || 'P4');
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedQuestion, setGeneratedQuestion] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedQuestion(null);
    
    try {
      const data = await api.generateQuestion(questionType, level, topic || null);
      setGeneratedQuestion(data);
      toast.success('Question generated successfully!');
    } catch (error) {
      console.error('Error generating question:', error);
      toast.error('Failed to generate question');
    } finally {
      setLoading(false);
    }
  };

  const handlePractice = () => {
    navigate(`/practice/${questionType}`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="ai-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-amber-600" data-testid="ai-generator-title">
                  AI Question Generator
                </h1>
                <p className="text-sm text-slate-500">Powered by GPT 5.2</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="p-8 rounded-3xl border-2 border-slate-200 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-amber-100 p-3 rounded-2xl">
                <Sparkles className="w-6 h-6 text-amber-600" strokeWidth={2.5} />
              </div>
              <h2 className="text-2xl font-heading font-bold text-slate-800">Customize Your Question</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Question Type</label>
                <Select value={questionType} onValueChange={setQuestionType}>
                  <SelectTrigger className="rounded-2xl border-2" data-testid="ai-question-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questionTypes.map(type => (
                      <SelectItem key={type.id} value={type.id} data-testid={`ai-type-${type.id}`}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Level</label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger className="rounded-2xl border-2" data-testid="ai-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P4" data-testid="ai-level-p4">Primary 4</SelectItem>
                    <SelectItem value="P5" data-testid="ai-level-p5">Primary 5</SelectItem>
                    <SelectItem value="P6" data-testid="ai-level-p6">Primary 6</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Topic (Optional)</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="w-full p-4 rounded-2xl border-2 border-slate-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                  placeholder="e.g., Present tense, Synonyms, Conjunctions..."
                  data-testid="ai-topic-input"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-amber-700"
                data-testid="ai-generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Question
                  </>
                )}
              </Button>
            </div>
          </Card>

          {generatedQuestion && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-8 rounded-3xl border-2 border-amber-200 bg-amber-50" data-testid="generated-question-card">
                <h3 className="text-xl font-heading font-bold text-slate-800 mb-4">Generated Question</h3>
                
                <div className="bg-white rounded-2xl p-6 mb-4">
                  <div className="inline-block bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold mb-3" data-testid="generated-topic">
                    {generatedQuestion.topic}
                  </div>
                  <p className="text-lg text-slate-800 whitespace-pre-wrap mb-4" data-testid="generated-question-text">
                    {generatedQuestion.question_text}
                  </p>
                  
                  {generatedQuestion.options && (
                    <div className="space-y-2 mb-4">
                      {generatedQuestion.options.map((option, index) => (
                        <div key={index} className="p-3 bg-slate-50 rounded-xl text-slate-700" data-testid={`generated-option-${index}`}>
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
                    <p className="text-sm font-bold text-emerald-700 mb-1">Correct Answer:</p>
                    <p className="text-slate-800" data-testid="generated-correct-answer">{generatedQuestion.correct_answer}</p>
                  </div>
                </div>

                <Button
                  onClick={handlePractice}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                  data-testid="start-practice-button"
                >
                  Start Practice Session
                </Button>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default AIGeneratorPage;